import React, { useEffect, useState, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Button, VStack, HStack, Box, Text, Input, Badge, Flex, Spinner, Wrap, WrapItem,
  Table, Thead, Tbody, Tr, Th, Td, useToast,
} from '@chakra-ui/react';
import { DownloadIcon, AddIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import {
  ImmTaller, ImmInscripcion,
  obtenerTaller, inscribirTaller, marcarAsistencia, obtenerListaAsistencia,
} from '../../../api/immApi';
import { IMM_COLOR, IMM_COLOR_HOVER, EstatusBadge } from '../_shared';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  taller: ImmTaller;
}

const ModalInscripciones: React.FC<Props> = ({ isOpen, onClose, taller }) => {
  const toast = useToast();
  const [inscripciones, setInscripciones] = useState<ImmInscripcion[]>([]);
  const [cargando, setCargando] = useState(false);
  const [nueva, setNueva] = useState({ nombre: '', telefono: '', email: '' });
  const [agregando, setAgregando] = useState(false);
  const [procesandoId, setProcesandoId] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    setCargando(true);
    try {
      const t = await obtenerTaller(taller.id);
      setInscripciones(t.inscripciones || []);
    } catch {
      setInscripciones([]);
    } finally {
      setCargando(false);
    }
  }, [taller.id]);

  useEffect(() => {
    if (isOpen) { fetchData(); setNueva({ nombre: '', telefono: '', email: '' }); }
  }, [isOpen, fetchData]);

  const handleAgregar = async () => {
    if (!nueva.nombre.trim()) {
      toast({ title: 'El nombre es obligatorio', status: 'warning', duration: 3000 });
      return;
    }
    setAgregando(true);
    try {
      await inscribirTaller(taller.id, {
        nombre: nueva.nombre,
        telefono: nueva.telefono || undefined,
        email: nueva.email || undefined,
      });
      toast({ title: 'Inscripción agregada', status: 'success', duration: 3000 });
      setNueva({ nombre: '', telefono: '', email: '' });
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo inscribir.', status: 'error', duration: 4000 });
    } finally {
      setAgregando(false);
    }
  };

  const handleAsistencia = async (insc: ImmInscripcion, estatus: string) => {
    setProcesandoId(insc.id);
    try {
      await marcarAsistencia(insc.id, estatus);
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo actualizar.', status: 'error', duration: 4000 });
    } finally {
      setProcesandoId(null);
    }
  };

  const handleDescargar = async () => {
    try {
      const lista = await obtenerListaAsistencia(taller.id);
      const filas = lista.inscripciones.map((i) => ({
        'Nombre': i.nombre,
        'Teléfono': i.telefono || '',
        'Origen': i.origenPortal ? 'Portal' : 'Admin',
        'Estatus': i.estatus,
      }));
      const ws = XLSX.utils.json_to_sheet(filas);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Lista de asistencia');
      XLSX.writeFile(wb, `Lista_Asistencia_${taller.id}.xlsx`);
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo generar la lista.', status: 'error', duration: 4000 });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>
          Inscripciones — {taller.nombre}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py={5}>
          <VStack spacing={4} align="stretch">
            {/* Mini-formulario agregar inscripción */}
            <Box borderWidth="1px" borderRadius="md" p={3} borderLeft={`3px solid ${IMM_COLOR}`}>
              <Text fontSize="sm" fontWeight="bold" color={IMM_COLOR} mb={2}>Agregar inscripción</Text>
              <Wrap spacing={2} align="flex-end">
                <WrapItem>
                  <Input size="sm" placeholder="Nombre*" value={nueva.nombre} w="200px"
                    onChange={(e) => setNueva((p) => ({ ...p, nombre: e.target.value }))} />
                </WrapItem>
                <WrapItem>
                  <Input size="sm" placeholder="Teléfono" value={nueva.telefono} w="150px"
                    onChange={(e) => setNueva((p) => ({ ...p, telefono: e.target.value }))} />
                </WrapItem>
                <WrapItem>
                  <Input size="sm" placeholder="Email" value={nueva.email} w="200px"
                    onChange={(e) => setNueva((p) => ({ ...p, email: e.target.value }))} />
                </WrapItem>
                <WrapItem>
                  <Button size="sm" leftIcon={<AddIcon />} bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }}
                    onClick={handleAgregar} isLoading={agregando} loadingText="Agregando...">
                    Agregar
                  </Button>
                </WrapItem>
              </Wrap>
            </Box>

            <Flex justify="space-between" align="center">
              <Text fontSize="sm" color="gray.500">Total inscritas: {inscripciones.length}</Text>
              <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={handleDescargar}>
                Descargar lista
              </Button>
            </Flex>

            <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
              <Table variant="simple" size="sm">
                <Thead bg={IMM_COLOR}>
                  <Tr>
                    <Th color="white" fontSize="xs">Nombre</Th>
                    <Th color="white" fontSize="xs">Teléfono</Th>
                    <Th color="white" fontSize="xs">Origen</Th>
                    <Th color="white" fontSize="xs">Estatus</Th>
                    <Th color="white" fontSize="xs">Acciones</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {cargando ? (
                    <Tr><Td colSpan={5} textAlign="center" py={8}><Spinner color={IMM_COLOR} /></Td></Tr>
                  ) : inscripciones.length === 0 ? (
                    <Tr><Td colSpan={5} textAlign="center" py={8}><Text color="gray.400">Sin inscripciones</Text></Td></Tr>
                  ) : (
                    inscripciones.map((insc, idx) => (
                      <Tr key={insc.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                        <Td><Text fontSize="sm" fontWeight="medium">{insc.nombre}</Text></Td>
                        <Td>{insc.telefono || '—'}</Td>
                        <Td>
                          <Badge colorScheme={insc.origenPortal ? 'teal' : 'gray'}>
                            {insc.origenPortal ? 'Portal' : 'Admin'}
                          </Badge>
                        </Td>
                        <Td><EstatusBadge value={insc.estatus} /></Td>
                        <Td>
                          <HStack spacing={1}>
                            <Button size="xs" colorScheme="green" variant="outline"
                              isLoading={procesandoId === insc.id}
                              onClick={() => handleAsistencia(insc, 'Asistio')}>Asistió</Button>
                            <Button size="xs" colorScheme="red" variant="outline"
                              isLoading={procesandoId === insc.id}
                              onClick={() => handleAsistencia(insc, 'NoAsistio')}>No asistió</Button>
                            <Button size="xs" colorScheme="teal" variant="outline"
                              isLoading={procesandoId === insc.id}
                              onClick={() => handleAsistencia(insc, 'Confirmada')}>Confirmar</Button>
                          </HStack>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalInscripciones;
